import type React from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

import { AppTooltip } from './AppTooltip'

interface AppButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  block?: boolean
  disabled?: boolean
  loading?: boolean
  title?: string
  ariaLabel?: string
  ariaPressed?: boolean
  buttonClass?: string
  onClick?: () => void
  onMouseEnter?: () => void
  onFocus?: () => void
  children: React.ReactNode
  type?: 'button' | 'submit' | 'reset'
}

export const AppButton: React.FC<AppButtonProps> = ({
  variant = 'primary',
  size = 'md',
  block = false,
  disabled = false,
  loading = false,
  title,
  ariaLabel,
  ariaPressed,
  buttonClass = '',
  onClick,
  onMouseEnter,
  onFocus,
  children,
  type = 'button',
}) => {
  const { t } = useTranslation()
  const variantClasses = `
    font-normal
    cursor-pointer
    rounded-button
    text-button
    leading-[1.5]
    transition-[color,background-color,border-color,box-shadow]
    disabled:pointer-events-none
    select-none
    ${variant === 'primary' ? 'text-white bg-primary border-button border-primary hover:bg-primary-hover hover:border-primary-hover active:bg-primary-hover active:border-primary-hover focus:shadow-focus-primary disabled:bg-primary disabled:border-primary disabled:text-white disabled:opacity-50' : ''}
    ${variant === 'secondary' ? 'text-white bg-secondary border-button border-transparent hover:bg-secondary-hover active:bg-secondary-hover focus:shadow-focus-secondary disabled:bg-secondary disabled:text-white disabled:opacity-50' : ''}
    ${variant === 'outline' ? 'text-gray-800 border-button border-border-gray bg-white hover:bg-brand-surface hover:border-primary active:bg-brand-surface active:border-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-500' : ''}
    ${variant === 'danger' ? 'text-white bg-danger border-button border-transparent hover:bg-danger-hover active:bg-danger-hover focus:shadow-focus-danger disabled:bg-danger disabled:text-white disabled:opacity-50' : ''}
    ${size === 'sm' ? 'py-button-padding-y-sm px-button-padding-x-sm text-sm' : ''}
    ${size === 'md' ? 'py-button-padding-y-md px-button-padding-x-md min-h-button-min-height' : ''}
    ${size === 'lg' ? 'py-button-padding-y-lg px-button-padding-x-lg text-lg min-h-button-min-height' : ''}
    ${block ? 'block w-full' : ''}
    ${disabled || loading ? 'cursor-not-allowed opacity-50' : ''}
    ${loading ? 'relative text-transparent' : ''}
  `
    .trim()
    .replace(/\s+/g, ' ')
  const baseClasses = twMerge(variantClasses, buttonClass)

  const button = (
    <button
      className={baseClasses}
      disabled={disabled || loading}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      type={type}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      aria-busy={loading || undefined}
    >
      {loading && (
        <span className="absolute top-1/2 left-1/2 -mt-2 -ml-2 text-white">
          <svg
            className="size-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </span>
      )}
      {children}
      {loading && <span className="sr-only"> — {t('common.loading')}</span>}
    </button>
  )

  if (title) {
    return <AppTooltip content={title}>{button}</AppTooltip>
  }

  return button
}
